/**
 * Kennis en leren (ontwerp v4 §8, plak 2.8; sessie 50).
 *
 * Vijf tabbladen: "Sanne leert van je aanpassingen", het gedragsdocument,
 * de waarheidskaarten (sessie 61), de bronnenbank met de kennisbank, en de
 * regels uit beslissingen.
 *
 * Sessie 53: het stoplicht staat niet meer hier maar in Beheer > Stoplicht
 * (werkplek v6); geen twee schermen voor dezelfde stand. Sessie 8D haalde
 * Beheer > Stoplicht weg; hier komt het niet terug.
 *
 * Sessie 61: het open tabblad staat in de url (`?tab=kaarten`), zodat
 * Beheer en een link in een melding direct op het goede blad openen.
 *
 * Sessie 50: de tabbladen staan op elke breedte. Het gedragsdocument
 * is te breed om als vierde smalle kolom naast de copiloot te passen;
 * één blad tegelijk leest beter dan vier kolommen van 200 px.
 *
 * Geen nieuwe motor: elk blok leest dezelfde service als het oude
 * scherm. Wat er bij komt is dat alles op één plek staat, en dat een
 * knop die niet kan de reden in beeld heeft staan.
 *
 * Het beantwoorden van een regelvoorstel gebeurt in de copiloot op
 * Vandaag, niet hier: twee wegen naar hetzelfde besluit geven twee
 * standen die uit elkaar lopen. Terugdraaien kan hier wel, en de
 * teruggedraaide regel blijft staan.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { vertaalApiFout } from "@/lib/api";
import { werkplekKennisApi } from "@/lib/werkplek-api";

import "./kennis.css";
import { MetCopiloot } from "../Copiloot/MetCopiloot";
import { GedragsdocumentTab } from "./GedragsdocumentTab";
import { WaarheidskaartenTab } from "./WaarheidskaartenTab";

/** Naam op de knop en sleutel in de url (`?tab=`). */
const TABS = [
  { naam: "Leren", sleutel: "leren" },
  { naam: "Gedragsdocument", sleutel: "gedrag" },
  { naam: "Waarheidskaarten", sleutel: "kaarten" },
  { naam: "Bronnen", sleutel: "bronnen" },
  { naam: "Regels", sleutel: "regels" },
] as const;

type TabSleutel = (typeof TABS)[number]["sleutel"];

const BRONCHIP: Record<string, string> = {
  verlopen: "wp-chip wp-chip-risico",
  binnenkort: "wp-chip wp-chip-wekker",
  geldig: "wp-chip wp-chip-whatsapp",
  geen: "wp-chip wp-chip-grijs",
};

const REGELCHIP: Record<string, string> = {
  voorstel: "wp-chip wp-chip-mail",
  actief: "wp-chip wp-chip-whatsapp",
  afgewezen: "wp-chip wp-chip-grijs",
  teruggedraaid: "wp-chip wp-chip-wekker",
};

function Stil({ tekst }: { tekst: string }) {
  return (
    <p className="wp-kn-stil" role="status">
      {tekst}
    </p>
  );
}

export function WerkplekKennis() {
  const [zoekParams, setZoekParams] = useSearchParams();
  const gevraagd = zoekParams.get("tab");
  const tab: TabSleutel =
    TABS.find((optie) => optie.sleutel === gevraagd)?.sleutel ?? "leren";
  function kiesTab(sleutel: TabSleutel) {
    const volgende = new URLSearchParams(zoekParams);
    volgende.set("tab", sleutel);
    setZoekParams(volgende, { replace: true });
  }
  const [zoek, setZoek] = useState("");
  const queryClient = useQueryClient();

  const leren = useQuery({
    queryKey: ["werkplek", "kennis", "leren"],
    queryFn: werkplekKennisApi.leren,
  });
  const bronnen = useQuery({
    queryKey: ["werkplek", "kennis", "bronnen"],
    queryFn: werkplekKennisApi.bronnen,
  });
  const kennisbank = useQuery({
    queryKey: ["werkplek", "kennis", "kennisbank", zoek],
    queryFn: () => werkplekKennisApi.kennisbank(zoek),
  });
  const regels = useQuery({
    queryKey: ["werkplek", "kennis", "regels"],
    queryFn: werkplekKennisApi.regels,
  });

  const terugdraaien = useMutation({
    mutationFn: (regelId: string) => werkplekKennisApi.draaiRegelTerug(regelId),
    onSuccess: async (regel) => {
      await queryClient.invalidateQueries({
        queryKey: ["werkplek", "kennis", "regels"],
      });
      toast.success(`"${regel.titel}" staat nu als teruggedraaid.`);
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>
          Kennis en leren
        </h1>
        {regels.data ? (
          <span style={{ fontSize: 12, color: "var(--wp-tekst3)" }}>
            {regels.data.actief} regels actief
            {regels.data.open_voorstellen > 0
              ? ` · ${regels.data.open_voorstellen} voorstel(len) open`
              : ""}
          </span>
        ) : null}
      </div>

      <div className="wp-inhoud">
        <MetCopiloot pagina="kennis">
        <div className="wp-kn-tabs" role="tablist" aria-label="Kennis en leren">
          {TABS.map((optie) => (
            <button
              key={optie.sleutel}
              type="button"
              role="tab"
              aria-selected={tab === optie.sleutel}
              className="wp-knop"
              onClick={() => kiesTab(optie.sleutel)}
            >
              {optie.naam}
            </button>
          ))}
        </div>

        <div className="wp-kn-bladen">
          {/* ── Blad 1: leren ─────────────────────────────────── */}
          <div className="wp-kn-blad" role="tabpanel" hidden={tab !== "leren"}>
            <div className="wp-blok">
              <h4>Sanne leert van je aanpassingen</h4>
              {leren.isLoading ? (
                <Stil tekst="Bezig met laden…" />
              ) : leren.isError || !leren.data ? (
                <Stil tekst="De leercijfers zijn niet op te halen." />
              ) : leren.data.reden_leeg ? (
                <p className="wp-kn-stil" style={{ padding: "10px 14px" }}>
                  {leren.data.reden_leeg}
                </p>
              ) : (
                <div style={{ padding: "10px 14px" }}>
                  <p style={{ margin: 0, fontSize: 13 }}>
                    <b style={{ fontSize: 20 }}>
                      {procent(leren.data.percentage_ongewijzigd)}
                    </b>{" "}
                    van {leren.data.totaal} concepten ging ongewijzigd de deur
                    uit. Je paste er {leren.data.gewijzigd} aan.
                  </p>
                  {leren.data.per_categorie.length > 0 ? (
                    <>
                      <p className="wp-kn-kop" style={{ marginTop: 12 }}>
                        Waar je het vaakst bijstuurt
                      </p>
                      {/* Balkjes in plaats van een kale lijst (JP, punt
                          37 van 22-09): met een staafje zie je in één
                          oogopslag wáár het zit. De schaal is de
                          hoogste categorie, niet het totaal — anders
                          is elk staafje een streepje. */}
                      <div className="wp-kn-staven">
                        {leren.data.per_categorie.slice(0, 6).map((rij) => (
                          <div className="wp-kn-staafrij" key={rij.categorie}>
                            <span className="wp-kn-staafnaam">
                              {rij.categorie}
                            </span>
                            <span className="wp-kn-staaf" aria-hidden>
                              <i
                                style={{
                                  width: `${
                                    (rij.aantal /
                                      Math.max(
                                        1,
                                        ...leren.data.per_categorie.map(
                                          (r) => r.aantal,
                                        ),
                                      )) *
                                    100
                                  }%`,
                                }}
                              />
                            </span>
                            <b className="wp-kn-staafgetal">{rij.aantal}</b>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* ── Blad 2: gedragsdocument ───────────────────────── */}
          <div className="wp-kn-blad" role="tabpanel" hidden={tab !== "gedrag"}>
            <GedragsdocumentTab />
          </div>

          {/* ── Blad 3: waarheidskaarten (sessie 61) ───────────── */}
          <div
            className="wp-kn-blad"
            role="tabpanel"
            hidden={tab !== "kaarten"}
            data-testid="wp-kn-blad-kaarten"
          >
            <WaarheidskaartenTab />
          </div>

          {/* ── Blad 4: bronnenbank en kennisbank ─────────────── */}
          <div className="wp-kn-blad" role="tabpanel" hidden={tab !== "bronnen"}>
            <p className="wp-kn-kop">
              Bronnenbank
              {bronnen.data ? ` · ${bronnen.data.totaal} bronnen` : ""}
            </p>
            <p className="wp-kn-plank-noot" data-testid="wp-kn-plank-noot">
              Sinds sessie 61 gaan deze passages niet meer in de prompt van
              Nova en Sanne; de goedgekeurde waarheidskaarten vervangen ze.
            </p>
            {bronnen.isLoading ? (
              <Stil tekst="Bezig met laden…" />
            ) : bronnen.isError || !bronnen.data ? (
              <Stil tekst="De bronnenbank is niet op te halen." />
            ) : bronnen.data.reden_leeg ? (
              <Stil tekst={bronnen.data.reden_leeg} />
            ) : (
              bronnen.data.bronnen.map((bron) => (
                <div className="wp-kn-kaart" key={bron.id}>
                  <div className="wp-kn-kaart-kop">
                    <span className={BRONCHIP[bron.status] ?? BRONCHIP.geen}>
                      {bron.soort}
                    </span>
                    <span className="wp-kn-kaart-titel">{bron.titel}</span>
                  </div>
                  <p className="wp-kn-kaart-waarom">
                    {bron.vindplaats ?? "geen vindplaats"} ·{" "}
                    {bron.aantal_chunks} stukken
                    {bron.zonder_embedding
                      ? " · nog niet doorzoekbaar: Sanne vindt deze bron niet"
                      : ""}
                    {bron.status === "verlopen" ? " · verlopen" : ""}
                    {!bron.actief ? " · uit" : ""}
                  </p>
                </div>
              ))
            )}

            <p className="wp-kn-kop" style={{ marginTop: 14 }}>
              Kennisbank
              {kennisbank.data ? ` · ${kennisbank.data.totaal} items` : ""}
            </p>
            <input
              className="wp-kn-zoek"
              value={zoek}
              onChange={(gebeurtenis) => setZoek(gebeurtenis.target.value)}
              placeholder="Zoek in de kennisbank"
              aria-label="Zoek in de kennisbank"
            />
            {kennisbank.isLoading ? (
              <Stil tekst="Bezig met laden…" />
            ) : kennisbank.isError || !kennisbank.data ? (
              <Stil tekst="De kennisbank is niet op te halen." />
            ) : kennisbank.data.reden_leeg ? (
              <Stil tekst={kennisbank.data.reden_leeg} />
            ) : (
              kennisbank.data.items.slice(0, 40).map((item) => (
                <div className="wp-kn-kaart" key={item.id}>
                  <div className="wp-kn-kaart-kop">
                    <span className="wp-chip wp-chip-grijs">
                      {item.categorie}
                    </span>
                    <span className="wp-kn-kaart-titel">{item.vraag}</span>
                  </div>
                  <p className="wp-kn-kaart-waarom">
                    {item.aantal_keer_gebruikt} keer gebruikt
                    {item.actief ? "" : " · uit"}
                  </p>
                  <div className="wp-kn-kaart-rij">
                    <a
                      className="wp-knop"
                      href={`/kennis/bewerken/${item.id}`}
                    >
                      Bewerken
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Blad 5: regels uit beslissingen ───────────────── */}
          <div className="wp-kn-blad" role="tabpanel" hidden={tab !== "regels"}>
            <p className="wp-kn-kop">Regels uit beslissingen</p>
            {regels.isLoading ? (
              <Stil tekst="Bezig met laden…" />
            ) : regels.isError || !regels.data ? (
              <Stil tekst="De regels zijn niet op te halen." />
            ) : regels.data.reden_leeg ? (
              <Stil tekst={regels.data.reden_leeg} />
            ) : (
              regels.data.regels.map((regel) => (
                <div className="wp-kn-kaart" key={regel.id}>
                  <div className="wp-kn-kaart-kop">
                    <span
                      className={REGELCHIP[regel.soort] ?? REGELCHIP.afgewezen}
                    >
                      {regel.label}
                    </span>
                    <span className="wp-kn-kaart-titel">{regel.titel}</span>
                  </div>
                  {regel.gezien ? (
                    <p className="wp-kn-kaart-waarom" data-regel-gezien>
                      {regel.gezien}
                    </p>
                  ) : null}
                  <p className="wp-kn-kaart-waarom">{regel.voorstel}</p>
                  {regel.onderbouwing ? (
                    <p className="wp-kn-kaart-waarom">{regel.onderbouwing}</p>
                  ) : null}
                  <div className="wp-kn-kaart-rij">
                    <button
                      type="button"
                      className="wp-knop"
                      disabled={
                        !regel.mag_terugdraaien || terugdraaien.isPending
                      }
                      onClick={() => terugdraaien.mutate(regel.id)}
                    >
                      Terugdraaien
                    </button>
                    {/* Uitgeschakeld is uitleggen waarom. */}
                    {regel.reden_uit ? (
                      <span
                        style={{ fontSize: 12, color: "var(--wp-tekst3)" }}
                      >
                        {regel.reden_uit}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </MetCopiloot>
      </div>
    </>
  );
}

/** "12,8%" — een komma, geen punt (JP, punt 36 van 22-09). */
function procent(waarde: number): string {
  return `${waarde.toLocaleString("nl-NL", { maximumFractionDigits: 1 })}%`;
}
